import React, { useState, useEffect } from "react";
import { Edit, Trash2, Plus, Save, X, Upload, AlertCircle, CheckCircle } from "lucide-react";
import { isAdminMode } from '../utils/auth'; // Authentication utility
import { Box, Fade, Typography } from "@mui/material";

interface Fact {
  id: number;
  number: string;
  label: string;
  image: string;
  colSpan: string;
}

const BASE_URL = import.meta.env.VITE_BASE_URL ?? '';

const FactAboutUs: React.FC = () => {
  const [facts, setFacts] = useState<Fact[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [editingFact, setEditingFact] = useState<Fact | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Form state
  const [formData, setFormData] = useState<Fact>({
    id: 0,
    number: '',
    label: '',
    image: '',
    colSpan: 'col-span-1'
  });
  const [newImagePreview, setNewImagePreview] = useState<string>('');

  useEffect(() => {
    fetchFacts();
  }, []);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const fetchFacts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}/api/Facts`);

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      setFacts(data);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch facts:", err);
      setError("Failed to load facts data. Please try again later.");
      showNotification("Failed to load facts data", 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message: string, type: 'success' | 'error') => {
    setNotification({ message, type });
  };

  const openCreateDialog = () => {
    setFormData({
      id: 0,
      number: '',
      label: '',
      image: '',
      colSpan: 'col-span-1'
    });
    setNewImagePreview('');
    setEditingFact(null);
    setIsEditing(false);
    setShowDialog(true);
  };

  const openEditDialog = (fact: Fact) => {
    setFormData({ ...fact });
    setNewImagePreview('');
    setEditingFact(fact);
    setIsEditing(true);
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
    setFormData({
      id: 0,
      number: '',
      label: '',
      image: '',
      colSpan: 'col-span-1'
    });
    setNewImagePreview('');
    setEditingFact(null);
    setIsEditing(false);
  };

  const handleInputChange = (field: keyof Fact, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setNewImagePreview(result);
        setFormData(prev => ({
          ...prev,
          image: result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    try {
      const url = isEditing
        ? `${BASE_URL}/api/Facts/${formData.id}`
        : `${BASE_URL}/api/Facts`;

      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      await fetchFacts();
      closeDialog();
      showNotification(
        isEditing ? 'Fact updated successfully!' : 'Fact created successfully!',
        'success'
      );
    } catch (err) {
      console.error('Error saving fact:', err);
      showNotification('Failed to save fact. Please try again.', 'error');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this fact?')) {
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/api/Facts/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      await fetchFacts();
      showNotification('Fact deleted successfully!', 'success');
    } catch (err) {
      console.error('Error deleting fact:', err);
      showNotification('Failed to delete fact. Please try again.', 'error');
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="flex flex-col items-center space-y-4">
          <div className="relative">
            <div className="w-12 h-12 border-4 border-blue-200 rounded-full animate-spin"></div>
            <div className="absolute top-0 left-0 w-12 h-12 border-4 border-blue-600 rounded-full animate-spin border-t-transparent"></div>
          </div>
          <p className="text-gray-600 font-medium">Loading facts...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-gradient-to-br from-slate-50 to-gray-100 p-6">
        <div className="bg-white rounded-2xl shadow-xl border border-red-100 p-8 max-w-md w-full">
          <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 text-center mb-2">Oops! Something went wrong</h3>
          <p className="text-gray-600 text-center">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className=" py-10 flex justify-center items-center w-full">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-6 right-6 z-50 p-4 rounded-xl shadow-2xl backdrop-blur-sm transition-all duration-300 transform ${
          notification.type === 'success'
            ? 'bg-emerald-50/95 text-emerald-800 border border-emerald-200'
            : 'bg-red-50/95 text-red-800 border border-red-200'
        }`}>
          <div className="flex items-center">
            {notification.type === 'success' ? (
              <CheckCircle className="w-5 h-5 mr-3 text-emerald-600" />
            ) : (
              <AlertCircle className="w-5 h-5 mr-3 text-red-600" />
            )}
            <span className="font-medium">{notification.message}</span>
          </div>
        </div>
      )}

      <div className=" md:w-[80%] w-[90%] ">
        {/* Header */}
        <Fade in timeout={800}>
            <Box className="text-center mb-12">
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' },
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 3,
                  letterSpacing: '-0.02em'
                }}
              >
                Facts About Us
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  fontSize: '1.1rem',
                  maxWidth: '600px',
                  mx: 'auto',
                  lineHeight: 1.6,
                  fontWeight: 400
                }}
              >
                {/* Here you can add facts about us and view the facts. */}
              </Typography>
            </Box>
          </Fade>

        {/* Facts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {facts.map((fact, index) => (
            <div
              key={fact.id}
              className={`group relative overflow-hidden rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 ${fact.colSpan}`}
              style={{
                animationDelay: `${index * 100}ms`,
                animation: 'fadeInUp 0.6s ease-out forwards'
              }}
            >
              {/* Background Image with Overlay */}
              <div className="relative h-[350px] overflow-hidden">
                <img
                  src={fact.image}
                  alt={fact.label}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors duration-500"></div>
              </div>

              {/* Content Overlay */}
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-8">
                <div className="text-center transform group-hover:scale-105 transition-transform duration-500">
                  <div className="font-black text-7xl lg:text-8xl xl:text-9xl mb-4 bg-gradient-to-b from-white to-white/80 bg-clip-text text-transparent drop-shadow-2xl">
                    {fact.number}
                  </div>
                  <div className="font-bold text-xl lg:text-2xl xl:text-3xl text-center leading-tight drop-shadow-lg">
                    {fact.label}
                  </div>
                </div>
              </div>

              {/* Admin Controls */}
              {isAdminMode() && (
                <div className="absolute top-6 right-6 flex gap-3 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-[-10px] group-hover:translate-y-0">
                  <button
                    onClick={() => openEditDialog(fact)}
                    className="bg-white/90 backdrop-blur-sm hover:bg-white p-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110"
                  >
                    <Edit className="w-4 h-4 text-gray-700" />
                  </button>
                  <button
                    onClick={() => handleDelete(fact.id)}
                    className="bg-white/90 backdrop-blur-sm hover:bg-white p-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Modern Dialog Modal */}
      {showDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
            onClick={closeDialog}
          ></div>
          
          {/* Modal */}
          <div className="relative bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden animate-in zoom-in duration-300">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-8 py-6 border-b border-gray-100">
              <div className="flex justify-between items-center">
                <h2 className="text-3xl font-bold text-gray-900">
                  {isEditing ? 'Edit Fact' : 'Create New Fact'}
                </h2>
                <button
                  onClick={closeDialog}
                  className="text-gray-400 hover:text-gray-600 hover:bg-white/50 p-2 rounded-full transition-all duration-200"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-8 overflow-y-auto max-h-[calc(90vh-200px)]">
              <div className="space-y-8">
                {/* Number/Statistic */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    Number/Statistic
                  </label>
                  <input
                    type="text"
                    value={formData.number}
                    onChange={(e) => handleInputChange('number', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 text-lg font-medium"
                    placeholder="e.g., 1500+, 133, 69"
                  />
                </div>

                {/* Label */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    Label
                  </label>
                  <input
                    type="text"
                    value={formData.label}
                    onChange={(e) => handleInputChange('label', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 text-lg"
                    placeholder="e.g., Students, Teachers & Staff"
                  />
                </div>

                {/* Column Span */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    Column Span
                  </label>
                  <select
                    value={formData.colSpan}
                    onChange={(e) => handleInputChange('colSpan', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200"
                  >
                    <option value="col-span-1">Single Column</option>
                    <option value="col-span-2">Double Column</option>
                  </select>
                </div>

                {/* Image Section */}
                <div className="space-y-4">
                  <label className="block text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    Image
                  </label>

                  {/* Current Image (when editing) */}
                  {isEditing && editingFact?.image && (
                    <div className="bg-gray-50 rounded-2xl p-4">
                      <p className="text-sm text-gray-600 mb-3 font-medium">Current Image:</p>
                      <img
                        src={editingFact.image}
                        alt="Current"
                        className="w-full h-48 object-cover rounded-xl border-2 border-gray-200"
                      />
                    </div>
                  )}

                  {/* New Image Preview */}
                  {newImagePreview && (
                    <div className="bg-blue-50 rounded-2xl p-4 border-2 border-blue-200">
                      <p className="text-sm text-blue-700 mb-3 font-semibold">New Image Preview:</p>
                      <img
                        src={newImagePreview}
                        alt="New preview"
                        className="w-full h-48 object-cover rounded-xl border-2 border-blue-300"
                      />
                    </div>
                  )}

                  {/* File Upload */}
                  <div className="border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center hover:border-blue-400 hover:bg-blue-50/30 transition-all duration-300">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                      id="image-upload"
                    />
                    <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <label
                      htmlFor="image-upload"
                      className="cursor-pointer inline-flex items-center px-6 py-3 border-2 border-gray-300 rounded-xl shadow-sm text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 hover:border-blue-400 transition-all duration-200"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {isEditing ? 'Upload New Image' : 'Upload Image'}
                    </label>
                    <p className="text-sm text-gray-500 mt-3">
                      Supported formats: JPG, PNG, WebP (Max 10MB)
                    </p>
                  </div>

                  {/* Manual URL Input */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-600">
                      Or enter image URL
                    </label>
                    <input
                      type="url"
                      value={newImagePreview ? '' : formData.image}
                      onChange={(e) => {
                        handleInputChange('image', e.target.value);
                        setNewImagePreview('');
                      }}
                      disabled={!!newImagePreview}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="https://example.com/image.jpg"
                    />
                    {newImagePreview && (
                      <p className="text-sm text-gray-500">
                        Clear the uploaded image to use URL instead
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="bg-gray-50 px-8 py-6 flex justify-end gap-4 border-t border-gray-100">
              <button
                onClick={closeDialog}
                className="px-6 py-3 text-gray-700 border-2 border-gray-200 rounded-xl hover:bg-gray-100 hover:border-gray-300 transition-all duration-200 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!formData.number || !formData.label || !formData.image}
                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 flex items-center font-semibold shadow-lg hover:shadow-xl"
              >
                <Save className="w-4 h-4 mr-2" />
                {isEditing ? 'Update Fact' : 'Create Fact'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style >{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-in {
          animation: slideIn 0.3s ease-out;
        }
        
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default FactAboutUs;